// Construction Phase Summaries
// Based on Red Setter Homes Master Project Tasks Template

export const constructionPhaseSummaries = {
  "In Development": {
    title: "In Development",
    shortDescription: "Plans, budget, and/or funding are being finalized",
    fullSummary:
      "Finalizing architectural and structural plans and budget before permitting begins. Coordinating with contractors and lenders to confirm scope, pricing, and site requirements. Preparing construction documents, contracts, and funding to ensure the project is ready for permit submission and build kickoff.",
    keyActivities: [
      "Finalize Architectural & Structural Plans",
      "Budget Confirmation & Value Engineering",
      "Site Review & Pre-Permit Coordination",
      "Financing & Contract Finalization",
      "Begin Material & Finish Selections Process",
    ],
    color: "grayLight1",
  },

  "Planning/Permitting": {
    title: "Planning/Permitting",
    shortDescription: "Pre-construction planning and permit acquisition",
    fullSummary:
      "Finalizing construction plans, obtaining necessary permits, and completing site preparation work including survey, stakeout, and utility coordination.",
    keyActivities: [
      "Review and finalize construction plans",
      "Site survey and stakeout",
      "Obtain building and septic permits",
      "Mark driveway location",
      "Water meter receipt processing",
    ],
    color: "grayLight2",
  },

  Foundation: {
    title: "Foundation",
    shortDescription: "Foundation construction and site utilities",
    fullSummary:
      "Excavation, grading, and foundation work including installation of under-slab plumbing and electrical. Setting up temporary utilities and completing waterproofing before pouring the foundation and passing inspection.",
    keyActivities: [
      "Complete excavation and grading",
      "Install foundation plumbing and electrical under slab",
      "Pour foundation",
      "Foundation inspection",
      "Set up temporary power and water",
      "Waterproofing for basement/crawlspace",
      "Gas connection coordination",
    ],
    color: "blueLight1",
  },

  Framing: {
    title: "Framing",
    shortDescription: "Structural framing and weatherproofing",
    fullSummary:
      "Building the structural skeleton of the home including wall framing, roof construction, and installation of exterior elements. The house is 'dried in' with weather barriers, windows, doors, and siding to protect the interior.",
    keyActivities: [
      "Deliver framing materials",
      "Frame walls and roof",
      "Wrap house with weather barrier",
      "Install windows and exterior doors",
      "Install siding",
      "House dried in",
    ],
    color: "blueLight2",
  },

  "Rough-In": {
    title: "Rough-In",
    shortDescription: "Mechanical, electrical, and plumbing systems",
    fullSummary:
      "Installation of all rough plumbing, electrical wiring, and HVAC systems before walls are closed. This includes running supply lines, drain pipes, electrical circuits, and ductwork throughout the home, followed by critical framing and MEP inspection.",
    keyActivities: [
      "Rough framing/MEP inspection",
      "Install interior door frames",
      "Cut and frame attic access",
      "Rough electrical installation",
      "Rough plumbing installation",
      "Rough HVAC installation",
      "Install fireplace unit",
      "Vent stacks through roof",
    ],
    color: "tealLight2",
  },

  "Insulation & Drywall": {
    title: "Insulation & Drywall",
    shortDescription: "Insulation and interior wall installation",
    fullSummary:
      "Installing insulation throughout the home for energy efficiency, followed by hanging and finishing drywall on all walls and ceilings. Includes insulation inspection to verify proper installation.",
    keyActivities: [
      "Install insulation in walls and ceilings",
      "Insulation inspection",
      "Drywall installation on walls and ceilings",
    ],
    color: "greenLight2",
  },

  "Trim & Paint": {
    title: "Trim & Paint",
    shortDescription: "Interior trim work and painting",
    fullSummary:
      "Installing all interior trim including baseboards, crown molding, and window/door casings. Priming and applying finish paint to all walls, ceilings, and trim throughout the home.",
    keyActivities: [
      "Install interior trim and moldings",
      "Prime walls and ceilings",
      "Apply finish paint coats",
    ],
    color: "yellowLight2",
  },

  "Mechanical Trim": {
    title: "Mechanical Trim",
    shortDescription: "Cabinets, countertops, and finish installations",
    fullSummary:
      "Installing major finish elements including cabinets, countertops, flooring, and fireplace shroud. Adding interior door hardware, cabinet hardware, and closet systems to complete the interior spaces.",
    keyActivities: [
      "Install cabinets",
      "Install kitchen countertops",
      "Install flooring",
      "Install cabinet hardware",
      "Install interior door hardware",
      "Install closet systems",
      "Install fireplace shroud",
    ],
    color: "orangeLight2",
  },

  "Final Finishes": {
    title: "Final Finishes",
    shortDescription: "Final installations and system connections",
    fullSummary:
      "Completing all final installations including plumbing fixtures, light fixtures, electrical outlets and switches, and HVAC components. Connecting utilities, conducting system tests, and completing exterior work like house numbers and landscaping.",
    keyActivities: [
      "Install electrical outlets and switches",
      "Install plumbing fixtures",
      "Install light fixtures and HVAC registers",
      "Connect and test HVAC system",
      "Final utility connections (electric, gas, water)",
      "Exterior and interior paint touch-ups",
      "Final cleaning",
      "Install house numbers and mailbox",
      "Landscaping",
      "Blower door test",
      "Pressure washing",
    ],
    color: "pinkLight2",
  },

  "Punch List": {
    title: "Punch List",
    shortDescription: "Quality control and corrections",
    fullSummary:
      "Conducting detailed walkthroughs to create comprehensive punch list of items requiring correction or completion. Testing all systems and completing all identified corrections to ensure quality standards are met.",
    keyActivities: [
      "Conduct pre-final walkthrough",
      "Create punch list items",
      "Complete punch list corrections",
      "Touch-up work as needed",
      "Test all systems functionality",
    ],
    color: "purpleLight2",
  },

  "Final Inspection / CO": {
    title: "Final Inspection / CO",
    shortDescription: "Final inspection and occupancy certificate",
    fullSummary:
      "Scheduling and passing final building inspection, addressing any inspection items, and obtaining the Certificate of Occupancy. Conducting final client walkthrough, transferring utilities, and closing out all permits.",
    keyActivities: [
      "Schedule final building inspection",
      "Address any inspection items",
      "Final client walkthrough",
      "Obtain Certificate of Occupancy",
      "Close out permits",
      "Remove dumpsters and excess materials",
      "Transfer utility accounts to homeowner",
    ],
    color: "grayLight2",
  },

  Complete: {
    title: "Complete",
    shortDescription: "Project completion and closeout",
    fullSummary:
      "Project is complete! Final photos taken, documentation provided to homeowner, and satisfaction survey conducted to ensure client happiness with their new home.",
    keyActivities: [
      "Final project photos",
      "Client satisfaction survey",
      "Deliver keys and documentation",
    ],
    color: "greenBright",
  },
};

// Helper function to get phase summary by name
export const getPhaseSummary = (phaseName) => {
  return constructionPhaseSummaries[phaseName] || null;
};

// Helper function to get all phase names in order
export const getPhaseNames = () => {
  return Object.keys(constructionPhaseSummaries);
};

// Helper function to get short description for display
export const getPhaseShortDescription = (phaseName) => {
  const phase = constructionPhaseSummaries[phaseName];
  return phase ? phase.shortDescription : "";
};

// Helper function to get full summary for display
export const getPhaseFullSummary = (phaseName) => {
  const phase = constructionPhaseSummaries[phaseName];
  return phase ? phase.fullSummary : "";
};

// Helper function to get key activities list
export const getPhaseKeyActivities = (phaseName) => {
  const phase = constructionPhaseSummaries[phaseName];
  return phase ? phase.keyActivities : [];
};
